// MODIFIED: Simplified 3-column History Modal with Change Tracking
import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'; // For the -> visual
import { formatDateLong } from "../../../../../../utils/textUtils";

export const EmployeeTransactionHistoryModal = ({ isOpen, onClose, data, employeeName }) => {
  if (!isOpen) return null;

  // Skip changes where the New Value is empty/N/A
  const getChangeList = (item) => {
    const changes = [];
    const check = (label, oldVal, newVal) => {
      const cleanOld = oldVal === "N/A" || !oldVal ? "" : oldVal.toString().trim();
      const cleanNew = newVal === "N/A" || !newVal ? "" : newVal.toString().trim();

      // ADDED: If there is no valid New Value, we skip this field entirely.
      // This prevents "Vessel: Vessel A -> None" from showing up if the vessel wasn't changed.
      if (cleanNew === "") return;

      changes.push({
        label,
        from: oldVal || "None",
        to: newVal // We know newVal is valid here
      });
    };

    // Mapping based on your Backend logic
    check("Via Captain", item.oldAssignedToName, item.assignedToName);
    check("Vessel", item.oldDestinationVesselName, item.destinationVesselName);
    check("Unit/Dept", item.oldDepartmentName, item.departmentName);
    check("Position", item.oldPositionName, item.tempPositionName);
    check("Employment", item.oldEmploymentName, item.employmentName);
    check("Pay Type", item.oldPayTypeName, item.payTypeName);
    check("Salary Type", item.oldSalaryType, item.salaryType);
    check("Rate", item.oldSalaryRateAmount, item.rateAmount);
    check("Rest Day", item.oldRestDay, item.restDay);
    check("Status", item.oldEmployeeStatusDescription, item.tempEmployeeStatusDescription);

    return changes;
  };

  return (
    <div role="dialog" className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
      <div className="bg-white w-full md:w-[90%] lg:w-[70%] rounded-lg shadow-2xl h-auto max-h-[90vh] flex flex-col relative">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50 rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg text-blue-900">
              <HistoryIcon />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Movement History</h2>
              <p className="text-xs text-gray-500 font-medium">{employeeName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer transition-colors">
            <CloseIcon />
          </button>
        </div>

        {/* Table Body */}
        <div className="overflow-y-auto flex-grow p-0">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-20 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 font-bold text-gray-600 uppercase text-[10px] tracking-wider w-1/4">Type / Code</th>
                <th className="px-6 py-4 font-bold text-gray-600 uppercase text-[10px] tracking-wider w-1/4">Effectivity</th>
                <th className="px-6 py-4 font-bold text-gray-600 uppercase text-[10px] tracking-wider">Changes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {data.length > 0 ? data.map((item) => {
                const changes = getChangeList(item);
                return (
                  <tr key={item.id} className="hover:bg-gray-50/50">
                    {/* Column 1: Type / Code */}
                    <td className="px-6 py-4 align-top">
                      <p className="font-bold text-blue-900 leading-tight">{item.employeeTransactionAccessCode}</p>
                      <p className="text-[11px] text-gray-500 mt-1 font-medium">{item.employeeTransactionAccessDescription}</p>
                      <span className="text-[9px] text-gray-300 font-mono mt-2 block">{item.transactionCode}</span>
                    </td>

                    {/* Column 2: Effectivity */}
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-700">{item.effectiveFrom ? formatDateLong(item.effectiveFrom) : "N/A"}</span>
                        <span className={`mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full border w-fit uppercase
                            ${item.status === 'Completed' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                          {item.status}
                        </span>
                      </div>
                    </td>

                    {/* Column 3: Changes (Only showing what moved) */}
                    <td className="px-6 py-4 align-top">
                      {changes.length > 0 ? (
                        <div className="space-y-3">
                          {changes.map((ch, idx) => (
                            <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 group">
                              <span className="text-[10px] font-bold text-gray-400 uppercase w-20 flex-shrink-0">{ch.label}</span>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-gray-400 line-through decoration-gray-300">{ch.from}</span>
                                <ArrowForwardIcon className="text-gray-300" style={{ fontSize: 14 }} />
                                <span className="font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{ch.to}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-300 italic text-xs">No administrative changes recorded</span>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="3" className="px-6 py-20 text-center text-gray-400 italic">No historical movements found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-8 py-2 bg-gray-800 text-white rounded font-bold text-sm hover:bg-black transition-colors border-none cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};